/**
 * User Service - API calls and Dexie storage management
 *
 * NOTE: This service manages user authentication and person selection.
 * User data is stored in Dexie localStorage table for offline persistence.
 */

import { API_ENDPOINTS } from '../../config/env';
import { CurrentUserResponse, SelectedPerson } from '../types/user';
import { getItem, setItem, removeItem } from './application_session_service';
import { clearDevice, getDeviceToken } from './device_service';
import { createLogger, LogLevel } from '../../lib/logger';
import { AuthExpiredError, fetchWithTimeout, NetworkError, TimeoutError } from '../../lib/fetch-utils';

const logger = createLogger('UserService', LogLevel.WARN);

// One AuthExpiredError class across every auth surface — callers branch
// on `instanceof`, so a second definition here would silently break that.
export { AuthExpiredError };

/**
 * Thrown when Mon Ami itself answers current_user with its record_not_found
 * error — local state is likely corrupt. A 404 without that body came from
 * something in front of the app (load balancer, maintenance page, a route
 * missing mid-deploy) and is not this error.
 */
export class UserNotFoundError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UserNotFoundError';
  }
}

// Dexie localStorage keys
export const CURRENT_PERSON_KEY = 'current_person';
export const CURRENT_USER_KEY = 'current_user';

/**
 * Fetch current user information from the API
 * Uses session-based authentication via cookies
 */
export async function fetchCurrentUser(): Promise<CurrentUserResponse> {
  try {
    // Prefer bearer auth when a device token has been registered. Fall back
    // to the Rails session cookie during the transition (and on the very
    // first call after sign-in, which is what registers the device in the
    // first place).
    const token = await getDeviceToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    let credentials: 'omit' | 'include' = 'include';
    if (token) {
      headers.Authorization = `Bearer ${token}`;
      credentials = 'omit';
    }

    const response = await fetchWithTimeout(API_ENDPOINTS.currentUser, {
      method: 'GET',
      headers,
      credentials,
      timeout: 10000, // 10 second timeout
    });

    if (!response.ok) {
      // Only 401 indicates session expiry across every auth surface in this
      // app (see the rule documented in auth.ts). 403 means "authenticated
      // but lacks permission" — different signal, must not clear identifiers
      // or device token. Surfaces as a generic Error so the caller decides.
      if (response.status === 401) {
        // Mirror fetchWithAuth's 401 cleanup so a 401 here doesn't leave a
        // stale current_user_id claiming the user is authenticated.
        // handleRefreshCurrentUser() also calls into this function and has
        // no surrounding cleanup of its own — centralizing the clear here
        // keeps the contract consistent across callers. Each clear is
        // wrapped so a transient Dexie failure can't replace the
        // AuthExpiredError signal with a DB exception.
        if (token) {
          try { await clearDevice(); } catch { /* swallow */ }
        }
        try { await removeItem(CURRENT_USER_KEY); } catch { /* swallow */ }
        try { await removeItem(CURRENT_PERSON_KEY); } catch { /* swallow */ }
        try { await removeItem('current_user_id'); } catch { /* swallow */ }
        throw new AuthExpiredError();
      }
      // UserNotFoundError can end in a device wipe, so only Mon Ami's own
      // answer counts (Api::Offline::BaseController#record_not_found). Any
      // other 404 falls through to the generic error, which callers treat as
      // "try again later".
      if (response.status === 404 && (await isRecordNotFoundBody(response))) {
        throw new UserNotFoundError('Current user not found on server. Local data may be out of sync.');
      }
      throw new Error(`Failed to fetch current user: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    // Friendlier messages for the login screen, but keep the error classes:
    // the data layer decides whether a sync failure is "not right now" (never
    // shown to the user, debounce released) by `instanceof` on these.
    if (error instanceof TimeoutError || error instanceof NetworkError) {
      logger.warn(`fetchCurrentUser: ${error.name}: ${error.message}`);
      error.message = error instanceof TimeoutError
        ? 'Request timed out. Please check your connection and try again.'
        : 'Network error. Please check your connection and try again.';
      throw error;
    }
    throw error;
  }
}

async function isRecordNotFoundBody(response: Response): Promise<boolean> {
  try {
    const body = await response.json();
    return body?.error === 'record_not_found';
  } catch {
    return false;
  }
}

/**
 * Get the currently selected person from Dexie
 */
export async function getCurrentPerson(): Promise<SelectedPerson | null> {
  return await getItem<SelectedPerson>(CURRENT_PERSON_KEY);
}

/**
 * Set the currently selected person in Dexie
 */
export async function setCurrentPerson(person: SelectedPerson): Promise<void> {
  await setItem(CURRENT_PERSON_KEY, person);
}

/**
 * Clear the current person from Dexie
 */
export async function clearCurrentPerson(): Promise<void> {
  await removeItem(CURRENT_PERSON_KEY);
}

/**
 * Check if a person is currently selected
 */
export async function hasCurrentPerson(): Promise<boolean> {
  const person = await getCurrentPerson();
  return person !== null;
}

/**
 * Get the current user data from storage
 */
export async function getCurrentUser(): Promise<CurrentUserResponse | null> {
  return await getItem<CurrentUserResponse>(CURRENT_USER_KEY);
}

/**
 * Set the current user data in storage
 */
export async function setCurrentUser(user: CurrentUserResponse): Promise<void> {
  await setItem(CURRENT_USER_KEY, user);
}

/**
 * Clear the current user data from storage
 */
export async function clearCurrentUser(): Promise<void> {
  await removeItem(CURRENT_USER_KEY);
}

/**
 * Refresh the current user and selected person from the API.
 * Fetches the latest user data and updates the stored person
 * (preserving the currently selected person by ID).
 */
export async function handleRefreshCurrentUser(): Promise<void> {
  const currentPerson = await getCurrentPerson();
  if (!currentPerson) return;

  const userData = await fetchCurrentUser();
  await setCurrentUser(userData);

  // Update the selected person with fresh data from the API
  const updatedPerson = userData.people?.find(p => p.id === currentPerson.id);
  if (updatedPerson) {
    await setCurrentPerson(updatedPerson);
  }
}
